/**
 * Image Fetcher Hook
 * React hook for fetching images with caching and loading states
 * Follows Single Responsibility Principle - handles only image fetching state
 */

import { useState, useCallback } from 'react';
import { imageService, ImageService } from '@/services/imageService';

export interface UseImageFetcherOptions {
  imageService?: ImageService;
  enabled?: boolean;
}

export interface UseImageFetcherResult {
  imageUrl: string | null;
  isLoading: boolean;
  error: Error | null;
  fetchImage: (
    query: string,
    options?: { description?: string; placeName?: string; category?: string }
  ) => Promise<string | null>;
}

/**
 * Hook for fetching images
 * @param options - Configuration options
 * @returns Image URL, loading state, error, and fetch function
 */
export function useImageFetcher(
  options: UseImageFetcherOptions = {}
): UseImageFetcherResult {
  const { imageService: service = imageService, enabled = true } = options;

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchImage = useCallback(
    async (
      query: string,
      options?: { description?: string; placeName?: string; category?: string }
    ): Promise<string | null> => {
      if (!enabled || !query || !query.trim()) {
        console.log(
          '🔍 [useImageFetcher] Skipping fetch - disabled or empty query:',
          {
            enabled,
            query,
          }
        );
        return null;
      }

      setIsLoading(true);
      setError(null);

      const requestDetails = {
        query: query.trim(),
        description: options?.description,
        placeName: options?.placeName,
        category: options?.category,
      };

      console.log('🖼️ [useImageFetcher] Fetching image:', requestDetails);

      try {
        const url = await service.fetchImage(query, options);

        if (url) {
          console.log('✅ [useImageFetcher] Image fetched successfully:', {
            query: query.trim(),
            url: url.substring(0, 100) + (url.length > 100 ? '...' : ''),
            category: options?.category,
          });
          setImageUrl(url);
          return url;
        } else {
          console.warn(
            '⚠️ [useImageFetcher] No image URL returned:',
            requestDetails
          );
          return null;
        }
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to fetch image');
        setError(error);
        console.error('❌ [useImageFetcher] Error fetching image:', {
          error: error.message,
          stack: error.stack,
          requestDetails,
        });
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [service, enabled]
  );

  return {
    imageUrl,
    isLoading,
    error,
    fetchImage,
  };
}
